import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { StylesManager } from 'survey-core';
import { withAITracking, AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ThemeProvider } from '@mui/material';
import { SurveyComponent as NewSurveyComponent } from './NewSurvey/SurveyComponent';
import { OldSurveyComponent } from './OldSurvey/OldSurveyComponent';
import Service from './Utilities/Service';
import SurveyType, { SurveyTypeValue } from './Data/SurveyType';
import Status, { StatusValue } from './Data/Status';
import Theme from './Data/Theme';
import { ErrorPage } from './Error/ErrorPage';
import { reactPlugin, appInsights } from './AppInsights';
import { LoadingScreen } from './components/LoadingScreen';
import handleServiceError, { isResponseLimitReached } from './Utilities/handleServiceError';
import theme from './theme';
import Languages from './Data/languages';
import { TurnstileWidget } from './components/TurnstileWidget';
import { MaintenancePage } from './pages/maintenance/MaintenancePage';
import { useRemoteConfig } from './utils/firebase/useRemoteConfig';
import {
  RemoteConfigKey,
  RemoteConfigKeyConfiguration,
  UnderMaintenanceConfigDev
} from './utils/firebase/remoteConfigKey';
import config from './config';
import type { Campaign, Respondent, SurveySchema, Template } from './types';

appInsights.trackPageView({ name: 'App.tsx' });
const queryParams = new URLSearchParams(globalThis.location.search);
const rating = queryParams.get('rating') ? Number(queryParams.get('rating')) : undefined;
const campaignMemberPublicGuid = queryParams.get('id');
const sha = queryParams.get('sha');
const campaign = queryParams.get('campaign');
const unsubscribe = (queryParams.get('unsubscribe') ?? '').toLowerCase() === 'true';
const enablePopup = Boolean(queryParams.get('enablePopup'));
const hideHeader = Boolean(queryParams.get('hideHeader'));
const hideFooter = Boolean(queryParams.get('hideFooter'));

const instanceId = queryParams.get('instanceId');

let surveyType: SurveyTypeValue = SurveyType.NORMAL;
if (sha) surveyType = SurveyType.UNIQUE;
else if (campaign) surveyType = SurveyType.UNIVERSAL;

const App: FunctionComponent = () => {
  const [respondent, setRespondent] = useState<Respondent | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [status, setStatus] = useState<StatusValue>(Status.SUCCESS);
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [surveySchema, setSurveySchema] = useState<SurveySchema | null>(null);
  const [surveyTheme, setSurveyTheme] = useState<number | null>(null);
  const [enableSmileys, setEnableSmileys] = useState(false);
  const [forceRatingResponsiveness, setForceRatingResponsiveness] = useState(false);
  const [forceRatingPrimaryColor, setForceRatingPrimaryColor] = useState(false);
  const [reduceSpaceAfterRatingQuestionInPanels, setReduceSpaceAfterRatingQuestionInPanels] =
    useState(false);
  const [surveyDoAnimation, setSurveyDoAnimation] = useState<boolean | null>(null);
  const [allowEditResponse, setAllowEditResponse] = useState(true);
  const [unsubscribedInitially, setUnsubscribedInitially] = useState(false);
  const [responseLimitReachedInitially, setResponseLimitReachedInitially] = useState(false);
  const [enableHumanVerification, setEnableHumanVerification] = useState<boolean | undefined>(
    undefined
  );
  const [humanVerificationWasSuccessful, setHumanVerificationWasSuccessful] = useState(false);
  const [shouldUseLocalStorage, setShouldUseLocalStorage] = useState(true);

  const { getRemoteConfigValue } = useRemoteConfig();
  const keyConfig: RemoteConfigKeyConfiguration = config.underMaintenanceKey.startsWith('#{')
    ? UnderMaintenanceConfigDev
    : { key: config.underMaintenanceKey as RemoteConfigKey, type: 'boolean' };
  const underMaintenance = getRemoteConfigValue(keyConfig);

  const applySurveyType = useCallback((incomingTemplate: Template) => {
    if (incomingTemplate.surveySchema) {
      const schema: SurveySchema =
        typeof incomingTemplate.surveySchema === 'string'
          ? JSON.parse(incomingTemplate.surveySchema)
          : incomingTemplate.surveySchema;

      StylesManager.applyTheme(schema.theme ?? 'defaultV2');
      setSurveyTheme((Theme as Record<string, number>)[schema.theme ?? 'defaultV2']);
      setEnableSmileys(schema.enableSmileys ?? false);
      setForceRatingResponsiveness(schema.forceRatingResponsiveness ?? false);
      setForceRatingPrimaryColor(schema.forceRatingPrimaryColor ?? false);
      setSurveySchema(schema);
      setSurveyDoAnimation(schema.animation ?? false);
      setReduceSpaceAfterRatingQuestionInPanels(
        schema.reduceSpaceAfterRatingQuestionInPanels ?? false
      );
    }

    // if language on template is not supported by frontend, set language to english
    const isTemplateLanguageAvailable = Boolean(
      Languages.SUBMIT?.[incomingTemplate?.language?.name]
    );
    const updatedTemplate: Template = {
      ...incomingTemplate,
      language: {
        ...incomingTemplate.language,
        name: isTemplateLanguageAvailable ? incomingTemplate?.language?.name : 'English'
      }
    };
    setTemplate(updatedTemplate);
  }, []);

  const applyLocalStorage = useCallback((source: Campaign | Respondent) => {
    const activateUniversalLinkLocalStorage =
      (source as Campaign)?.activateUniversalLinkLocalStorage ?? false;
    const isNotUniversalLink = campaignMemberPublicGuid
      ? true
      : surveyType !== SurveyType.UNIVERSAL &&
        !(source as Campaign).universalLinkCampaign &&
        !activateUniversalLinkLocalStorage;

    setShouldUseLocalStorage(isNotUniversalLink || activateUniversalLinkLocalStorage);
  }, []);

  const prepareNormalSurvey = useCallback(
    async (service: Service) => {
      if (!campaignMemberPublicGuid) {
        handleServiceError(Status.MISSINGGUID, setStatus, setErrorDetails);
        return;
      }
      try {
        const r = await service.getResponse();
        if (r.response?.closed || !r.campaign?.acceptingResponses) setAllowEditResponse(false);
        setRespondent(r);
        const tmpl = r.campaign?.template;
        setEnableHumanVerification(r.enableHumanVerification);
        if (tmpl) applySurveyType(tmpl);
        if (r.campaign) applyLocalStorage(r.campaign);
      } catch {
        handleServiceError(Status.UNKNOWN, setStatus, setErrorDetails);
      }
    },
    [applySurveyType, applyLocalStorage]
  );

  const prepareUniquePersonalizedLinkSurvey = useCallback(
    async (service: Service) => {
      if (!sha) {
        handleServiceError(Status.MISSINGGUID, setStatus, setErrorDetails);
        return;
      }
      try {
        await service.addUniquePersonalizedLinkResponse();
        const r = await service.getResponse();
        if (r.response?.closed || !r.campaign?.acceptingResponses) {
          setAllowEditResponse(false);
        }
        setRespondent(r);
        const tmpl = r.campaign?.template;
        setEnableHumanVerification(r.enableHumanVerification);
        if (tmpl) applySurveyType(tmpl);
        if (r.campaign) applyLocalStorage(r.campaign);
      } catch (e) {
        handleServiceError(
          isResponseLimitReached(e) ? Status.RESPONSE_LIMIT_REACHED : Status.UNKNOWN,
          setStatus,
          setErrorDetails
        );
      }
    },
    [applySurveyType, applyLocalStorage]
  );

  const prepareUniversalLinkSurvey = useCallback(
    async (service: Service) => {
      if (!campaign) {
        handleServiceError(Status.MISSINGGUID, setStatus, setErrorDetails);
        return;
      }
      try {
        const r = await service.getCampaignInfo();
        setResponseLimitReachedInitially(r.responseLimitReached ?? false);
        setRespondent(r);
        const tmpl = r.template;
        setEnableHumanVerification(r.enableHumanVerification);
        if (tmpl) applySurveyType(tmpl);
        applyLocalStorage(r);
      } catch {
        handleServiceError(Status.UNKNOWN, setStatus, setErrorDetails);
      }
    },
    [applySurveyType, applyLocalStorage]
  );

  useEffect(() => {
    const prepareSurvey = async () => {
      const service = new Service().proxy;

      const unsubscribeRequestedButHumanVerificationNotComplete =
        unsubscribe && !humanVerificationWasSuccessful;
      if (unsubscribeRequestedButHumanVerificationNotComplete) {
        setEnableHumanVerification(true);
        return;
      }

      const unsubcribeRequestedAndHumanVerificationComplete =
        unsubscribe && humanVerificationWasSuccessful && !unsubscribedInitially;
      if (unsubcribeRequestedAndHumanVerificationComplete) {
        await service.unsubscribe();
        setUnsubscribedInitially(true);
        setAllowEditResponse(false);
      }

      if (campaignMemberPublicGuid && surveyType === SurveyType.NORMAL) {
        const campaignMemberExpirationStatus = await service.evaluateSurveyExpirationStatus();
        if (campaignMemberExpirationStatus.expired) {
          const error = {
            status: Status.SURVEYEXPIRED,
            errorDetails:
              campaignMemberExpirationStatus.surveyExpiredMessage ??
              'The survey has expired and is no longer accepting responses.'
          };
          handleServiceError(error, setStatus, setErrorDetails);
          return;
        }
      }

      switch (surveyType) {
        case SurveyType.NORMAL:
          await prepareNormalSurvey(service);
          break;
        case SurveyType.UNIQUE:
          await prepareUniquePersonalizedLinkSurvey(service);
          break;
        case SurveyType.UNIVERSAL:
          await prepareUniversalLinkSurvey(service);
          break;
        default:
          handleServiceError(Status.UNKNOWN, setStatus, setErrorDetails);
      }
    };

    if (campaignMemberPublicGuid) {
      appInsights.setAuthenticatedUserContext(campaignMemberPublicGuid);
    }

    prepareSurvey();
  }, [
    humanVerificationWasSuccessful,
    prepareNormalSurvey,
    prepareUniquePersonalizedLinkSurvey,
    prepareUniversalLinkSurvey,
    unsubscribedInitially
  ]);

  if (underMaintenance) {
    return (
      <AppInsightsContext.Provider value={reactPlugin}>
        <ThemeProvider theme={theme}>
          <MaintenancePage />
        </ThemeProvider>
      </AppInsightsContext.Provider>
    );
  }

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <ThemeProvider theme={theme}>
        {status === Status.SUCCESS ? (
          <>
            {enableHumanVerification && (
              <TurnstileWidget
                humanVerificationWasSuccessful={humanVerificationWasSuccessful}
                setHumanVerificationWasSuccessful={setHumanVerificationWasSuccessful}
              />
            )}
            {/**
             * If the initial human verification is not complete, show the loading screen
             * If isInteractionRequired, dont show loading, as the human verification component is shown
             *
             * If the initial human verification is complete, show the survey component - which also triggers any initial saving of responses
             */}

            {(humanVerificationWasSuccessful || !enableHumanVerification) && (
              <>
                {respondent && template?.surveySchema && status === Status.SUCCESS && (
                  <NewSurveyComponent
                    respondent={respondent}
                    template={template}
                    surveyType={surveyType}
                    rating={rating}
                    acceptingResponses={Boolean(
                      respondent.acceptingResponses ?? respondent.campaign?.acceptingResponses
                    )}
                    campaignMemberPublicGuid={campaignMemberPublicGuid}
                    campaign={campaign}
                    surveyTheme={surveyTheme}
                    enableSmileys={enableSmileys}
                    forceRatingResponsiveness={forceRatingResponsiveness}
                    forceRatingPrimaryColor={forceRatingPrimaryColor}
                    reduceSpaceAfterRatingQuestionInPanels={reduceSpaceAfterRatingQuestionInPanels}
                    surveySchema={surveySchema as SurveySchema}
                    surveyDoAnimation={surveyDoAnimation}
                    setStatus={setStatus}
                    allowEditResponse={allowEditResponse}
                    unsubscribedInitially={unsubscribedInitially}
                    responseLimitReachedInitially={responseLimitReachedInitially}
                    enablePopup={enablePopup}
                    instanceId={instanceId}
                    setErrorDetails={setErrorDetails}
                    shouldUseLocalStorage={shouldUseLocalStorage}
                    hideFooter={unsubscribe || hideFooter}
                    hideHeader={hideHeader}
                  />
                )}
                {respondent && template && !template.surveySchema && status === Status.SUCCESS && (
                  <OldSurveyComponent
                    respondent={respondent}
                    template={template}
                    surveyType={surveyType}
                    rating={rating}
                    campaignMemberPublicGuid={campaignMemberPublicGuid}
                    campaign={campaign}
                    setStatus={setStatus}
                    allowEditResponse={allowEditResponse}
                    unsubscribedInitially={unsubscribedInitially}
                    responseLimitReachedInitially={responseLimitReachedInitially}
                    enablePopup={enablePopup}
                    instanceId={instanceId}
                    setErrorDetails={setErrorDetails}
                    shouldUseLocalStorage={shouldUseLocalStorage}
                    hideFooter={hideFooter}
                    hideHeader={hideHeader}
                  />
                )}
                {!(respondent && template) && <LoadingScreen />}
              </>
            )}
          </>
        ) : (
          <ErrorPage status={status} setStatus={setStatus} errorDetails={errorDetails} />
        )}
      </ThemeProvider>
    </AppInsightsContext.Provider>
  );
};

export default withAITracking(reactPlugin, App);
